import { useCallback, useEffect, useRef, useState } from "react";
import { listMyTrainingGroups, registerSessionCacheReset, type TrainingGroupRef } from "@/api";
import { createLogger } from "@/logging";

const logger = createLogger("page/base/account");

export type SidebarLoadStatus = "idle" | "loading" | "ready" | "error";

export type SidebarLoadState<T> = {
  status: SidebarLoadStatus;
  items: readonly T[];
};

export type SidebarAccountData = {
  // 소속 그룹. 조직 헤더의 요약 라인에 쓴다.
  groups: SidebarLoadState<TrainingGroupRef>;
  // 계정 팝업이 처음 열릴 때 호출한다. 세션당 한 번만 요청한다.
  ensureLoaded: () => void;
};

// 세션 동안 1회만 받는다(페이지를 이동할 때마다 다시 조회하지 않는다). 세션 경계에서는
// resetSessionCatalogs 가 비운다.
type Loader<T> = {
  cache: readonly T[] | null;
  inflight: Promise<readonly T[]> | null;
  // 세션(사용자) 세대. clear 할 때 증가시킨다. 그래서 clear 이전에 시작된 in-flight 응답이 새 세대
  // 캐시를 덮어쓰지 않는다.
  epoch: number;
};

const groupLoader: Loader<TrainingGroupRef> = { cache: null, inflight: null, epoch: 0 };

function loadOnce<T>(
  loader: Loader<T>,
  fetcher: () => Promise<readonly T[]>,
): Promise<readonly T[]> {
  if (loader.cache) {
    return Promise.resolve(loader.cache);
  }
  if (!loader.inflight) {
    const startEpoch = loader.epoch;
    loader.inflight = fetcher()
      .then((items) => {
        // clear 이후(다른 세대)에 도착한 응답은 캐시에 반영하지 않는다. 사용자 전환 후 stale 값을 막는다.
        if (loader.epoch === startEpoch) {
          loader.cache = items;
        }
        return items;
      })
      .finally(() => {
        if (loader.epoch === startEpoch) {
          loader.inflight = null;
        }
      });
  }
  return loader.inflight;
}

// 세션 경계에서 호출한다. 캐시를 비운다. 그래서 다음 사용자가 (full reload 없이) 로그인해도 이전
// 사용자의 소속 그룹이 남지 않는다. epoch 를 올려 진행 중 요청이 늦게 캐시에 반영되는 것도 막는다.
export function clearSidebarAccountCache(): void {
  clearLoader(groupLoader);
}

registerSessionCacheReset(clearSidebarAccountCache);

function clearLoader<T>(loader: Loader<T>): void {
  loader.epoch += 1;
  loader.cache = null;
  loader.inflight = null;
}

export function useSidebarAccountData(): SidebarAccountData {
  const [groups, setGroups] = useState<SidebarLoadState<TrainingGroupRef>>(() =>
    initialState(groupLoader),
  );
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const ensureLoaded = useCallback(() => {
    load(groupLoader, listMyTrainingGroups, setGroups, mountedRef, "groups");
  }, []);

  return { groups, ensureLoaded };
}

function initialState<T>(loader: Loader<T>): SidebarLoadState<T> {
  return loader.cache ? { status: "ready", items: loader.cache } : { status: "idle", items: [] };
}

function load<T>(
  loader: Loader<T>,
  fetcher: () => Promise<readonly T[]>,
  setState: (state: SidebarLoadState<T>) => void,
  mountedRef: { current: boolean },
  label: string,
): void {
  if (loader.cache) {
    setState({ status: "ready", items: loader.cache });
    return;
  }
  setState({ status: "loading", items: [] });
  loadOnce(loader, fetcher)
    .then((items) => {
      if (!mountedRef.current) return;
      setState({ status: "ready", items });
    })
    .catch((error: unknown) => {
      logger.error(`Failed to load sidebar ${label}.`, error);
      if (!mountedRef.current) return;
      setState({ status: "error", items: [] });
    });
}
